import { EcommerceItem, PurchaseUserData } from "../../../types/google-tag-manager";

// Helper function to safely parse track item data
function parseTrackItem(element: Element | null): EcommerceItem | null {
    if (!element) return null;

    try {
        const data = up.element.jsonAttr(element, 'ng-track-item');
        if (!data || typeof data !== 'object') {
            console.warn('Invalid track item data:', data);
            return null;
        }

        return mapToEcommerceItem(data);
    } catch (error) {
        console.error('Failed to parse track item:', error);
        return null;
    }
}

// Helper function to map raw data to EcommerceItem
function mapToEcommerceItem(data: any, item_category: string = "subscription"): EcommerceItem {
    return {
        item_id: String(data.pricePointId || ''),
        item_name: String(data.name || ''),
        price: Number(data.price || ''),
        item_category: String(item_category)
    };
}

function hasValidCheckoutItems(items: unknown): items is EcommerceItem[] {
    return Array.isArray(items) && items.length > 0 && items.every((item: any) =>
        item && typeof item === 'object'
        && (typeof item.item_id === 'string' && item.item_id.length > 0
            || typeof item.item_name === 'string' && item.item_name.length > 0)
        && Number.isFinite(item.price)
    );
}

function parsePurchaseUserData(userData: unknown): PurchaseUserData | undefined {
    if (!userData || typeof userData !== 'object' || Array.isArray(userData)) return undefined;

    const data = userData as Record<string, unknown>;
    const validHash = (value: unknown): value is string =>
        typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
    const parsed: PurchaseUserData = {};

    if (validHash(data.sha256_email)) parsed.sha256_email = data.sha256_email;
    if (validHash(data.sha256_phone_google)) parsed.sha256_phone_google = data.sha256_phone_google;
    if (validHash(data.sha256_phone_meta)) parsed.sha256_phone_meta = data.sha256_phone_meta;

    return Object.keys(parsed).length > 0 ? parsed : undefined;
}

// Helper function to safely get attribute value
function getAttributeOrEmpty(element: Element | null, attrName: string): string {
    return element?.getAttribute(attrName) || '';
}

up.compiler('[ng-track-listview]', (element: HTMLElement, data, meta) => {
    if (meta.revalidating) return;

    const list_id = getAttributeOrEmpty(element, 'ng-track-list-id');
    const list_name = getAttributeOrEmpty(element, 'ng-track-list-name');

    const items: EcommerceItem[] = Array.from(element.querySelectorAll('[ng-track-item]'))
        .map(item => parseTrackItem(item))
        .filter((item): item is EcommerceItem => item !== null);

    if (items.length === 0) {
        console.warn('ng-track-listview: No valid items found');
        return;
    }

    trackListView(list_id, list_name, items);
});

up.compiler('[ng-track-selection]', (element: HTMLElement): (() => void) | void => {
    const clickHandler = (event: Event) => {
        const listNode = element.closest('[ng-track-listview]');
        if (!listNode) {
            console.warn('ng-track-selection: No parent list found');
            return;
        }

        const list_id = getAttributeOrEmpty(listNode, 'ng-track-list-id');
        const list_name = getAttributeOrEmpty(listNode, 'ng-track-list-name');

        const itemNode = element.closest('[ng-track-item]');
        const item = parseTrackItem(itemNode);

        if (!item) {
            console.warn('ng-track-selection: Invalid item data');
            return;
        }

        trackSelectItem(item, list_id, list_name);
    };

    const unsub = up.on(element, 'click', clickHandler);

    return () => {
        unsub();
    };
});

up.compiler('[ng-track-addtocart]', (element: HTMLElement): (() => void) | void => {
    const clickHandler = (event: Event) => {
        const itemNode = element.closest('[ng-track-item]');
        const item = parseTrackItem(itemNode);

        if (!item) {
            console.warn('ng-track-addtocart: Invalid item data');
            return;
        }

        trackAddToCart(item, item.price);
    };

    const unsub = up.on(element, 'click', clickHandler);

    return () => {
        unsub();
    };
});

up.compiler('[ng-track-begincheckout]', (element: HTMLElement, data, meta) => {
    if (meta.revalidating) return;

    try {
        const jsonItem = up.element.jsonAttr(element, 'ng-track-begincheckout');

        if (!jsonItem || typeof jsonItem.checkoutSessionId !== 'string' || !jsonItem.checkoutSessionId
            || !Array.isArray(jsonItem.items) || jsonItem.items.length === 0) {
            console.error('ng-track-begincheckout: Invalid data structure');
            return;
        }

        const items = jsonItem.items.map((item: any) => mapToEcommerceItem(item));
        if (!hasValidCheckoutItems(items)) {
            console.error('ng-track-begincheckout: Invalid checkout items');
            return;
        }
        let checkoutSnapshot = { ...jsonItem, items, lastShippingAddressKey: null };
        let shouldTrackBeginCheckout = true;

        const existingCheckoutData = sessionStorage.getItem('__ecommerce_checkout');
        if (existingCheckoutData) {
            try {
                const existingSnapshot = JSON.parse(existingCheckoutData);
                if (existingSnapshot.checkoutSessionId === jsonItem.checkoutSessionId
                    && hasValidCheckoutItems(existingSnapshot.items)) {
                    checkoutSnapshot = existingSnapshot;
                    shouldTrackBeginCheckout = false;
                }
            } catch (error) {
                console.warn('ng-track-begincheckout: Existing checkout snapshot is invalid:', error);
            }
        }

        if (shouldTrackBeginCheckout) {
            sessionStorage.setItem('__ecommerce_checkout', JSON.stringify(checkoutSnapshot));
            trackBeginCheckout(items, jsonItem.value, jsonItem.currency);
        }

        const updateCheckoutSnapshot = (update: (snapshot: any) => void): any | null => {
            const stored = sessionStorage.getItem('__ecommerce_checkout');
            if (!stored) return null;

            try {
                const snapshot = JSON.parse(stored);
                if (snapshot.checkoutSessionId !== jsonItem.checkoutSessionId
                    || !hasValidCheckoutItems(snapshot.items)) return null;

                update(snapshot);
                sessionStorage.setItem('__ecommerce_checkout', JSON.stringify(snapshot));
                return snapshot;
            } catch (error) {
                console.error('ng-track-begincheckout: Failed to update checkout data:', error);
                return null;
            }
        };

        const onShippingAccepted = (event: Event) => {
            const detail = (event as CustomEvent<{ checkoutSessionId: string; addressKey: string }>).detail;
            if (!detail || detail.checkoutSessionId !== jsonItem.checkoutSessionId || !detail.addressKey) return;

            let shouldTrack = false;
            const snapshot = updateCheckoutSnapshot(current => {
                if (current.lastShippingAddressKey !== detail.addressKey) {
                    current.lastShippingAddressKey = detail.addressKey;
                    shouldTrack = true;
                }
            });
            if (snapshot && shouldTrack) trackCheckoutShipping(snapshot);
        };

        const onPaymentSubmitted = (event: Event) => {
            const detail = (event as CustomEvent<{ checkoutSessionId: string; paymentType: string }>).detail;
            if (!detail || detail.checkoutSessionId !== jsonItem.checkoutSessionId || !detail.paymentType) return;
            const snapshot = updateCheckoutSnapshot(() => {});
            if (snapshot) trackCheckoutPayment(snapshot, detail.paymentType);
        };

        const onSummaryUpdated = (event: Event) => {
            const detail = (event as CustomEvent<{ checkoutSessionId: string; value: number; tax: number; shipping: number }>).detail;
            if (!detail || detail.checkoutSessionId !== jsonItem.checkoutSessionId) return;
            updateCheckoutSnapshot(snapshot => {
                snapshot.value = detail.value;
                snapshot.tax = detail.tax;
                snapshot.shipping = detail.shipping;
            });
        };

        window.addEventListener('ngenic:checkout-shipping-accepted', onShippingAccepted);
        window.addEventListener('ngenic:checkout-payment-submitted', onPaymentSubmitted);
        window.addEventListener('ngenic:checkout-summary-updated', onSummaryUpdated);

        return () => {
            window.removeEventListener('ngenic:checkout-shipping-accepted', onShippingAccepted);
            window.removeEventListener('ngenic:checkout-payment-submitted', onPaymentSubmitted);
            window.removeEventListener('ngenic:checkout-summary-updated', onSummaryUpdated);
        };
    } catch (error) {
        console.error('ng-track-begincheckout: Failed to process checkout data:', error);
    }
});

up.compiler('[ng-track-purchase]', (element: HTMLElement, data, meta) => {
    if (meta.revalidating) return;

    try {
        const purchaseJson = up.element.jsonAttr(element, 'ng-track-purchase');
        if (!purchaseJson || typeof purchaseJson.checkoutSessionId !== 'string'
            || typeof purchaseJson.transactionId !== 'string' || !purchaseJson.transactionId.trim()) {
            console.error('ng-track-purchase: Invalid purchase data');
            return;
        }

        const checkoutData = sessionStorage.getItem('__ecommerce_checkout');
        if (!checkoutData) {
            console.warn('ng-track-purchase: No checkout data found in session');
            return;
        }

        const checkoutJson = JSON.parse(checkoutData);

        if (!checkoutJson || checkoutJson.checkoutSessionId !== purchaseJson.checkoutSessionId
            || !hasValidCheckoutItems(checkoutJson.items)) {
            console.error('ng-track-purchase: Invalid checkout data structure');
            return;
        }

        const markerKey = `__ecommerce_purchase_${encodeURIComponent(purchaseJson.transactionId)}`;
        if (localStorage.getItem(markerKey)) {
            sessionStorage.removeItem('__ecommerce_checkout');
            return;
        }

        const items: EcommerceItem[] = checkoutJson.items;

        trackPurchase(
            items,
            purchaseJson.transactionId,
            checkoutJson.value,
            checkoutJson.tax,
            checkoutJson.currency,
            checkoutJson.shipping,
            purchaseJson.campaignCode || undefined,
            parsePurchaseUserData(purchaseJson.userData)
        );

        localStorage.setItem(markerKey, '1');
        sessionStorage.removeItem('__ecommerce_checkout');
    } catch (error) {
        console.error('ng-track-purchase: Failed to parse checkout data:', error);
    }
});

up.compiler('[ng-tracks-login]', function () {
    trackLogin();
});

function trackCheckoutShipping(snapshot: any): void {
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
        event: 'add_shipping_info',
        ecommerce: {
            currency: snapshot.currency,
            value: snapshot.value,
            items: snapshot.items
        }
    });
}

function trackCheckoutPayment(snapshot: any, paymentType: string): void {
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
        event: 'add_payment_info',
        ecommerce: {
            currency: snapshot.currency,
            value: snapshot.value,
            payment_type: paymentType,
            items: snapshot.items
        }
    });
}

/**
 * Track item list view event
 * @param list_id - Unique identifier for the list
 * @param list_name - Human-readable name for the list
 * @param items - Array of items in the list
 */
function trackListView(list_id: string, list_name: string, items: EcommerceItem[], currency: string = "SEK"): void {
    if (items.length === 0) {
        console.warn('trackListView: No items to track');
        return;
    }

    dataLayer.push({ ecommerce: null });
    dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
            currency: currency,
            item_list_id: list_id,
            item_list_name: list_name,
            items: items
        }
    });
}

/**
 * Track item selection event
 * @param item - Selected item
 * @param list_id - Unique identifier for the list containing the item
 * @param list_name - Human-readable name for the list
 */
function trackSelectItem(item: EcommerceItem, list_id: string, list_name: string, currency: string = "SEK"): void {
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
        event: 'select_item',
        ecommerce: {
            currency: currency,
            value: item.price,
            item_list_id: list_id,
            item_list_name: list_name,
            items: [item]
        }
    });
}

/**
 * Track add to cart event
 * @param item - Item being added to the cart
 * @param currency - Currency code (e.g., 'SEK', defaults to 'SEK')
 * @param value - Value of the item
 */
function trackAddToCart(item: EcommerceItem, value: number, currency: string = "SEK"): void {
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
            currency: currency,
            value: value,
            items: [item]
        }
    });
}

/**
 * Track begin checkout event
 * @param items - Items in the checkout
 * @param total - Total value of the checkout
 * @param currency - Currency code (e.g., 'SEK')
 */
function trackBeginCheckout(items: EcommerceItem[], total: number, currency: string = "SEK"): void {
    if (items.length === 0) {
        console.warn('trackBeginCheckout: No items to track');
        return;
    }

    dataLayer.push({ ecommerce: null });
    dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
            items: items,
            value: total,
            currency: currency
        }
    });
}

/**
 * Track purchase event
 * @param items - Purchased items
 * @param transaction_id - Unique transaction identifier
 * @param total - Total value of the purchase
 * @param tax - Tax amount
 * @param currency - Currency code (defaults to 'SEK')
 */
function trackPurchase(
    items: EcommerceItem[],
    transaction_id: string,
    total: number,
    tax: number,
    currency: string = "SEK",
    shipping: number = 0,
    campaignCode?: string,
    userData?: PurchaseUserData
): void {
    if (items.length === 0) {
        console.warn('trackPurchase: No items to track');
        return;
    }

    dataLayer.push({ ecommerce: null, user_data: null });
    dataLayer.push({
        event: 'purchase',
        ...(userData ? { user_data: userData } : {}),
        ecommerce: {
            transaction_id: transaction_id,
            currency: currency,
            value: total,
            tax: tax,
            shipping: shipping,
            coupon: null,
            ...(campaignCode ? { campaign_code: campaignCode } : {}),
            items: items
        }
    });
}

/**
 * Track login event
 */
function trackLogin(): void {
    dataLayer.push({
        event: 'login',
        method: "OpenID Connect"
    });
}
